import React, { useMemo, useState } from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { findCategory, capAllowed, categoryVisible } from '@/config/adminNav';
import { ChevronLeft, Search, ArrowRight, Lock } from 'lucide-react';

export default function AdministrationCategoryPage() {
  const { categoryId } = useParams();
  const { user, hasPermission, permsLoaded } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const category = findCategory(categoryId);
  const CatIcon = category?.icon;
  const hubPath = category?.audience === 'platform_console' ? '/platform-console' : '/administration';
  const hubLabel = category?.audience === 'platform_console' ? 'Platform Console' : 'Administration';

  // Deep-link into a function carrying `?from=<categoryId>` so the destination
  // page can render a "back to category" bar (Layout → AdminReturnBar).
  const withFrom = (to) => {
    const [path, qs] = String(to).split('?');
    const params = new URLSearchParams(qs || '');
    params.set('from', categoryId);
    return `${path}?${params.toString()}`;
  };

  const items = useMemo(() => {
    if (!category) return [];
    const visible = category.items.filter((it) => capAllowed(it, user, hasPermission));
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((it) => it.label.toLowerCase().includes(q) || (it.description || '').toLowerCase().includes(q));
  }, [category, user, hasPermission, query]);

  // Unknown category → back to Administration. Otherwise wait for permissions
  // to hydrate before deciding access (avoids a flash redirect for super admins).
  if (!category) {
    return <Navigate to="/administration" replace />;
  }
  if (permsLoaded && !categoryVisible(category, user, hasPermission)) {
    return <Navigate to={hubPath} replace />;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid={`administration-category-${category.id}`}>
      {/* Breadcrumb / header */}
      <button
        onClick={() => navigate(hubPath)}
        className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground mb-4 transition-colors"
        data-testid="admin-category-back"
      >
        <ChevronLeft className="w-4 h-4" /> {hubLabel}
      </button>

      <div className="flex items-start gap-4 mb-6">
        <div className={`w-14 h-14 rounded-2xl bg-muted/40 flex items-center justify-center ${category.accent}`}>
          <CatIcon className="w-7 h-7" strokeWidth={1.6} />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground tracking-tight">{category.label}</h1>
          <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
        </div>
      </div>

      {/* Search within category */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${category.label.toLowerCase()}…`}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card/60 border border-border/40 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
          data-testid="admin-category-search"
        />
      </div>

      {/* Items */}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="admin-category-items">
        {items.map((it) => {
          const testId = `admin-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          if (it.future) {
            return (
              <div
                key={it.label}
                className="flex items-center gap-4 p-4 rounded-xl border border-border/30 bg-card/30 opacity-60"
                data-testid={testId}
              >
                <div className="w-10 h-10 rounded-xl bg-muted/30 flex items-center justify-center text-muted-foreground flex-shrink-0">
                  <it.icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground flex items-center gap-2">
                    {it.label}
                    <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">Coming soon</span>
                  </div>
                  <div className="text-[12px] text-muted-foreground">{it.description}</div>
                </div>
                <Lock className="w-4 h-4 text-muted-foreground/80 flex-shrink-0" />
              </div>
            );
          }
          return (
            <Link
              key={it.label}
              to={withFrom(it.to)}
              className="group flex items-center gap-4 p-4 rounded-xl border border-border/40 bg-card/50 hover:border-primary/40 hover:bg-card transition-all duration-200"
              data-testid={testId}
            >
              <div className="w-10 h-10 rounded-xl bg-muted/40 flex items-center justify-center text-primary flex-shrink-0">
                <it.icon className="w-5 h-5" strokeWidth={1.6} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground">{it.label}</div>
                <div className="text-[12px] text-muted-foreground">{it.description}</div>
              </div>
              <span className="flex items-center gap-1 text-[12px] font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                Open <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          );
        })}
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground col-span-full py-10 text-center">No functions match &quot;{query}&quot;.</p>
        )}
      </div>
    </div>
  );
}
